<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { AddIcon, HistoryIcon, UndoLeftRoundIcon } from '@solar-icons/vue/linear'
import AppPage from '../../shell/AppPage/AppPage.vue'
import AppAction from '../../primitives/AppActions/AppAction.vue'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import AppChatPanel from '../AppChatPanel/AppChatPanel.vue'
import AppConversation, { CONVERSATION_MEASURE_CLASS } from '../AppConversation/AppConversation.vue'
import ChatComposer from './ChatComposer.vue'
import ChatDesignSuggestions from './ChatDesignSuggestions.vue'
import ChatLiveTranscript from './ChatLiveTranscript.vue'
import HomeMarkRelief from '../../home/HomeMarkRelief/HomeMarkRelief.vue'
import { vCoach } from '../../tour/v-coach'
import { useChatStore } from '@/stores/chat'
import { useChatThreadSwitcher } from '@/composables/useChatThreadSwitcher'
import { useChatDock } from '../AppChatPanel/useChatDock'
import { useStageStore } from '@/stores/stage'
import { useToastStore } from '@/stores/toast'
import { isLoaded } from '@/types/load-state'

type ChatSurfaceVariant = 'full' | 'docked'

/** The one true state of the surface, whatever the message list happens to be doing. */
type ChatSurfaceState = 'resolving' | 'empty' | 'conversation'

const props = withDefaults(defineProps<{ variant?: ChatSurfaceVariant }>(), { variant: 'full' })

const chat = useChatStore()
const router = useRouter()
const stage = useStageStore()
const toast = useToastStore()
const { t } = useI18n()
const { openDock, closeDock, panel } = useChatDock()
const { newChat } = useChatThreadSwitcher()

const isStreamingTurn = computed(
  () => chat.sendStatus === 'submitting' || chat.sendStatus === 'streaming',
)

const chatState = computed<ChatSurfaceState>(() => {
  if (!isLoaded(chat.messages)) return 'resolving'
  if (chat.messages.value.length === 0 && !isStreamingTurn.value) return 'empty'
  return 'conversation'
})

const showTranscript = computed(() => chatState.value !== 'resolving')

function openAllChats(): void {
  void router.push({ name: 'chats' })
}

watch(
  () => stage.context,
  (context) => {
    if (context) openDock()
  },
)

watch(
  () => chat.attachment,
  (attachment) => {
    if (attachment) openDock()
  },
)

watch(
  () => chat.threads,
  (state) => {
    if (state.status === 'error') toast.error(t('chat.errors.loadThreadsFailed'), state.message)
  },
  { immediate: true },
)

watch(
  () => chat.error,
  (message) => {
    if (message) toast.error(t('errors.somethingWentWrong'), message)
  },
)

chat.openThreads()

const isDocked = computed(() => props.variant === 'docked')

watch(
  isDocked,
  (docked) => {
    if (!docked) closeDock()
  },
  { immediate: true },
)
</script>

<template>
  <div v-if="isDocked" v-show="panel === 'chat'" class="h-full">
    <AppChatPanel
      :label="t('chat.dock.askRaldo')"
      :empty="chatState === 'empty'"
      :thread-key="chat.activeThreadId"
    >
      <template #header-actions>
        <AppAction
          size="sm"
          v-if="stage.canReturn"
          :label="t('chat.backToWhereIWas')"
          :icon="UndoLeftRoundIcon"
          @click="stage.back"
        />
        <AppAction size="sm" :label="t('chat.nav.newChat')" :icon="AddIcon" @click="newChat" />
      </template>

      <ChatLiveTranscript v-if="showTranscript" />

      <template #composer>
        <ChatDesignSuggestions />
        <ChatComposer />
      </template>
    </AppChatPanel>
  </div>

  <AppPage v-else bleed full-height class="motion-safe:animate-view-in">
    <div class="relative flex min-h-0 flex-1 flex-col">
      <div class="absolute right-12 top-12 z-20">
        <AppButton
          variant="ghost"
          size="xs"
          :start-icon="HistoryIcon"
          :label="t('chat.nav.seeAll')"
          @click="openAllChats"
        />
      </div>

      <div v-if="chatState === 'empty'" class="@container relative h-full">
        <div class="pointer-events-none absolute inset-0 overflow-hidden">
          <HomeMarkRelief />
        </div>
        <div class="relative flex h-full items-center justify-center">
          <div :class="['relative bottom-32 mx-auto w-full', CONVERSATION_MEASURE_CLASS]">
            <ChatComposer v-coach="'home.composer'" data-arrival="content" welcome />
          </div>
        </div>
      </div>

      <AppConversation
        v-else
        :log-label="t('chat.conversationLabel')"
        :scroll-to-bottom-label="t('chat.scrollToBottom')"
        :thread-key="chat.activeThreadId"
      >
        <ChatLiveTranscript v-if="showTranscript" />

        <template #composer><ChatComposer v-coach="'home.composer'" /></template>
      </AppConversation>
    </div>
  </AppPage>
</template>
